import React, { useEffect, useState } from 'react'

import { NavigationDatePicker } from 'cube/components/NavigationDatePicker'
import { parseISO } from 'date-fns'
import { contains } from 'ramda'
import { useLocation } from 'react-router-dom'

import {
  Box,
  Flex,
  Icon,
  Link,
  Select,
  SimpleGrid,
  Table,
  TableColumn,
  Text,
  ToggleButton,
  ToggleButtonGroup,
} from '@driftt/dds'
import { faArrowUpRightFromSquare } from '@driftt/dds.icons/light/faArrowUpRightFromSquare'
import {
  ChartCard,
  CircularChart,
  CubeQueryStringAdapter,
  parseCubeQueryParams,
} from '@driftt/dds.reports'
import { NavigationHeader } from '@driftt/tide-core'

import { StyledMainContentContainer } from 'layouts/CubeReportsLayout/constants'
import { OpportunitiesTableRow } from 'layouts/RevenueReport'
import {
  ClosedWon,
  OpenRevenuePipeline,
  SalesCycle,
  TotalRevenuePipeline,
} from 'layouts/RevenueReport/Overview/RevenueKPICards'
import { ROI_OPPORTUNITY_REPORT_PATH } from 'layouts/RevenueReport/RevenueReport.constants'
import { hasVisualOppotunityTimeline } from 'utils/access'
import { isValidISOString } from 'utils/dates'
import { TypeFormats } from 'utils/format'

import {
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
  OverviewStoreProvider,
  useOverviewStore,
} from './Overview.context'
import useOpportunities from './Overview.hook'
import { ROIDataType } from './Overview.types'

const RevenueOverviewReportContent = () => {
  const hasRoiV3Access = hasVisualOppotunityTimeline() // TODO: Remove when released
  const { search } = useLocation()
  const {
    setStore,
    store: { startDate, endDate },
    updateStore,
  } = useOverviewStore()

  const [selectedValues, setSelectedValues] = useState<string[]>([
    ROIDataType.INFLUENCED,
  ])

  const handleControlledOnToggleButtonClick = (values: string[]) => {
    setSelectedValues([values[0] || ROIDataType.INFLUENCED])
  }

  const isInfluenced = !!contains(ROIDataType.INFLUENCED, selectedValues)
  const isSourced = !!contains(ROIDataType.SOURCED, selectedValues)

  const {
    topOpportunitiesData,
    opportunityTypesSeriesData,
    isLoadingTopOpportunitiesData,
    isLoadingTypesCubeData,
    totalCountOfFilteredOpportunitites,
    topOpportunitiesDataCubeQuery,
  } = useOpportunities({
    isInfluenced,
    isSourced,
  })

  const topOpportunitiesTableColumns: TableColumn<OpportunitiesTableRow>[] = [
    {
      id: 'firstEnduserInteractionAtDate',
      value: 'companyName',
      header: '',
      cell: ({ companyName }) => <Text variant="body">{companyName}</Text>,
      disableSort: true,
    },
    {
      id: 'firstEnduserInteractionAtDay',
      value: 'opportunityType',
      header: '',
      cell: ({ opportunityType }) => (
        <Text variant="metadata">{opportunityType}</Text>
      ),
      disableSort: true,
    },
    {
      id: 'conversationUrl',
      value: 'opportunityAmount',
      header: '',
      cell: ({ opportunityAmount }) => (
        <Text variant="bodyBold">
          {TypeFormats.CURRENCY_UNROUNDED(opportunityAmount)}
        </Text>
      ),
      disableSort: true,
    },
  ]

  const drillDownQueryString = CubeQueryStringAdapter.toQueryString(
    topOpportunitiesDataCubeQuery
  )

  /**
   * Initialize OverviewStore on mount. Use valid query param values or defaults.
   */
  useEffect(() => {
    const { startDate, endDate } = parseCubeQueryParams(search, {
      ignoreQueryPrefix: true,
      arrayLimit: 100,
    })

    setStore({
      startDate: isValidISOString(startDate)
        ? parseISO(startDate)
        : DEFAULT_START_DATE,
      endDate: isValidISOString(endDate) ? parseISO(endDate) : DEFAULT_END_DATE,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <Box className="reports-layout">
        <NavigationHeader title="Opportunity overview">
          <NavigationDatePicker
            startDate={startDate}
            endDate={endDate}
            updateDates={updateStore}
          />
        </NavigationHeader>
        <StyledMainContentContainer
          fullWidth
          fullHeight
          direction="column"
          gap={2}
        >
          <Flex
            fullWidth
            fullHeight
            justifyContent="space-between"
            alignItems="flex-end"
            direction="row"
            gap={2}
          >
            {hasRoiV3Access && (
              <Select
                label="View opportunities by"
                placeholder="Select..."
                options={[
                  {
                    label: 'Site concierge',
                    value: 'site concierge',
                  },
                  {
                    label: 'AI generated emails',
                    value: 'ai generated emails',
                  },
                  {
                    label: 'Chat',
                    value: 'chats',
                  },
                  {
                    label: 'Fastlane',
                    value: 'fastlane',
                  },
                  {
                    label: 'Video',
                    value: 'video',
                  },
                ]}
                onChange={() => {
                  return
                }}
              />
            )}
            <Flex
              fullWidth
              fullHeight
              justifyContent="flex-end"
              direction="row"
              gap={2}
            >
              <ToggleButtonGroup
                initialSelectedValues={[ROIDataType.INFLUENCED]}
                color="blue"
                exclusive
                appearance="outline"
                controlledSelectedValues={selectedValues}
                onChange={handleControlledOnToggleButtonClick}
              >
                <ToggleButton
                  value={ROIDataType.INFLUENCED}
                  appearance="outline"
                >
                  Influenced
                </ToggleButton>
                <ToggleButton value={ROIDataType.SOURCED} appearance="outline">
                  Sourced
                </ToggleButton>
              </ToggleButtonGroup>
            </Flex>
          </Flex>
          <SimpleGrid fullWidth columns={hasRoiV3Access ? 4 : 3} gap={3}>
            {hasRoiV3Access && (
              <TotalRevenuePipeline
                isInfluenced={isInfluenced}
                isSourced={isSourced}
              />
            )}
            <OpenRevenuePipeline
              isInfluenced={isInfluenced}
              isSourced={isSourced}
            />
            <ClosedWon isInfluenced={isInfluenced} isSourced={isSourced} />
            <SalesCycle isInfluenced={isInfluenced} isSourced={isSourced} />
          </SimpleGrid>
          <SimpleGrid fullWidth columns={2} gap={3}>
            <ChartCard>
              <ChartCard.Header
                title="Opportunities"
                tooltip="Includes Closed Won, Closed Lost, and Open opportunities."
                subtitle="What types of opportunities has Drift influenced?"
              />
              <ChartCard.Content>
                <CircularChart
                  series={[
                    {
                      data: opportunityTypesSeriesData,
                      label: 'Opportunities',
                    },
                  ]}
                  isLoading={isLoadingTypesCubeData}
                  chartType="DONUT"
                  title={`${totalCountOfFilteredOpportunitites}`}
                  subtitle="Opportunities"
                />
              </ChartCard.Content>
            </ChartCard>
            <ChartCard>
              <Flex direction="row" justifyContent="space-between">
                <ChartCard.Header
                  title={
                    hasRoiV3Access
                      ? 'Pipeline by product line'
                      : 'Top opportunities'
                  }
                  subtitle={
                    hasRoiV3Access
                      ? 'Pipeline generated by site visitors who have engaged with any product line'
                      : 'What are the biggest opportunities Drift has influenced?'
                  }
                />
                <Link
                  target="_blank"
                  variant="inline"
                  href={`${ROI_OPPORTUNITY_REPORT_PATH}?${drillDownQueryString}`}
                >
                  <Icon icon={faArrowUpRightFromSquare} /> View data
                </Link>
              </Flex>
              <ChartCard.Content as={Flex} fullHeight>
                <Table<OpportunitiesTableRow>
                  isLoading={isLoadingTopOpportunitiesData}
                  loadingRows={5}
                  rows={topOpportunitiesData}
                  columns={topOpportunitiesTableColumns}
                  disableBoxShadow
                  hideHeader
                  emptyState={{
                    title: 'You don’t have any data yet',
                    subtitle: 'Please enable opportunities to sync',
                  }}
                />
              </ChartCard.Content>
            </ChartCard>
          </SimpleGrid>
        </StyledMainContentContainer>
      </Box>
    </>
  )
}

export const RevenueOverviewReport = () => {
  return (
    <OverviewStoreProvider>
      <RevenueOverviewReportContent />
    </OverviewStoreProvider>
  )
}
